/**
 * Reusable lobby code display with copy-to-clipboard.
 *
 * Shows the code in large monospace font with a glow effect
 * and a button that copies to clipboard on click.
 */

import { AnimatePresence, motion } from "framer-motion";
import { Check, Copy } from "lucide-react";
import { useState } from "react";
import { cn } from "../../lib/utils";

type LobbyCodeDisplayProps = {
  code: string;
  label?: string;
};

export function LobbyCodeDisplay({ code, label }: LobbyCodeDisplayProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback: do nothing
    }
  };

  return (
    <div className="relative group">
      {/* Glow background */}
      <div className="absolute -inset-1 rounded-xl bg-gradient-to-r from-brand-500/15 via-brand-400/25 to-brand-500/15 blur-md opacity-80" />

      <button
        onClick={handleCopy}
        className={cn(
          "relative flex items-center gap-4 px-6 @md:px-8 py-4 rounded-xl cursor-pointer",
          "bg-surface-800 border-2 border-brand-500/30",
          "hover:border-brand-500/50 hover:shadow-[0_0_30px_rgba(38,217,181,0.15)]",
          "transition-all duration-200",
          "focus:outline-none focus:ring-2 focus:ring-brand-400 focus:ring-offset-2 focus:ring-offset-surface-900",
        )}
      >
        <div className="flex flex-col items-start gap-0.5">
          {label && (
            <span className="text-[9px] font-display uppercase tracking-widest text-surface-500">
              {label}
            </span>
          )}
          <span className="text-2xl @sm:text-3xl font-mono font-bold text-brand-400 tracking-[0.15em] select-all">
            {code}
          </span>
        </div>

        {/* Copy icon */}
        <div className="shrink-0">
          <AnimatePresence mode="wait">
            {copied ? (
              <motion.div
                key="check"
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0 }}
                className="w-8 h-8 rounded-full bg-brand-500/15 flex items-center justify-center"
              >
                <Check className="h-4 w-4 text-brand-400" strokeWidth={2.5} />
              </motion.div>
            ) : (
              <motion.div
                key="copy"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="w-8 h-8 rounded-full bg-surface-700/60 flex items-center justify-center group-hover:bg-brand-500/15 transition-colors"
              >
                <Copy className="h-3.5 w-3.5 text-surface-400 group-hover:text-brand-400 transition-colors" />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </button>

      {/* Copy feedback */}
      <AnimatePresence>
        {copied ? (
          <motion.p
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-xs text-brand-400 whitespace-nowrap"
          >
            Copied to clipboard
          </motion.p>
        ) : (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-xs text-surface-500 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity"
          >
            Click to copy
          </motion.p>
        )}
      </AnimatePresence>
    </div>
  );
}
